// eslint-disable-next-line @nx/enforce-module-boundaries
import { EShortcutIdentifier, iconMap, IShortcutItem, ShortcutsStore, TShortcutOption } from '@shared/components/shortcuts/data-access';
import { Component, ElementRef, EventEmitter, inject, effect, Output, signal, ViewChild, untracked, AfterViewInit, computed } from '@angular/core';

import { IconButtonComponent } from '@shared/components/ui/icon-button';
import { DropdownSwitchComponent, TDropdownSwitch } from '@ac-monorepo-fend/dropdown-switch';
import { RouterStore } from '@route/data-access';
import {
  ProfileStore,
} from '@person/profile/data-access';
import { TooltipDirective, TooltipStaticDirective } from '@shared/directives';
import { TranslateModule } from '@ngx-translate/core';
import { FilegroupStore } from '@shared/filegroup/data-access';
/**
 * Shortcut component that can be used to display icon buttons or a list of actions.
 */
@Component({
  selector: 'lib-shortcuts',
  standalone: true,
  imports: [
    IconButtonComponent,
    DropdownSwitchComponent,
    TooltipDirective,
    TooltipStaticDirective,
    TranslateModule,
  ],
  templateUrl: './shortcuts.component.html',
  styleUrl: './shortcuts.component.css',
})
export class ShortcutsComponent implements AfterViewInit {
  public readonly shortcutsStore = inject(ShortcutsStore);
  public readonly filegroupStore = inject(FilegroupStore);
  public readonly shortcuts = this.shortcutsStore.shortcuts;
  public readonly profileStore = inject(ProfileStore);
  public readonly isShortcutsHidden = this.shortcutsStore.isShortcutsHidden();

  /**
   * List of options available for selection in the dropdown-list when the type is 'action'.
   */
  public listOptions: TShortcutOption[] = [
    { id: '1', description: 'COMPONENTS.SHORTCUTS.NEW_CUSTOMER', isChecked: true, state: 'disabled', identifier: EShortcutIdentifier.NewClient,
      tabs: computed(() => this.shortcutsStore.unsavedDataItems()?.filter(p => p.type === EShortcutIdentifier.NewClient)),
    },
    { id: '2', description: 'COMPONENTS.SHORTCUTS.LAUNCH_SUBSCRIPTION', isChecked: true, state: 'disabled', identifier: EShortcutIdentifier.NewSubscription },
    { id: '3', description: 'COMPONENTS.SHORTCUTS.LAUNCH_CONSUMPTION', isChecked: false, state: 'default', identifier: EShortcutIdentifier.Consumption },
    { id: '4', description: 'COMPONENTS.SHORTCUTS.LAUNCH_SERVICE', isChecked: false, state: 'default', identifier: EShortcutIdentifier.LaunchService },
    { id: '5', description: 'COMPONENTS.SHORTCUTS.LAUNCH_SALE', isChecked: false, state: 'default', identifier: EShortcutIdentifier.NewSale },
    { id: '6', description: 'COMPONENTS.SHORTCUTS.NEW_VISITOR', isChecked: false, state: 'default', identifier: EShortcutIdentifier.NewVisitor,
      tabs: computed(() => this.shortcutsStore.unsavedDataItems()?.filter(p => p.type === EShortcutIdentifier.NewVisitor)),
   },
    { id: '7', description: 'COMPONENTS.SHORTCUTS.MAKE_PAYMENT', isChecked: false, state: 'default', identifier: EShortcutIdentifier.MakePayment },
    { id: '8', description: 'COMPONENTS.SHORTCUTS.MAKE_RECEIVEMENT', isChecked: false, state: 'default', identifier: EShortcutIdentifier.MakeReceipt },
    { id: '9', description: 'COMPONENTS.SHORTCUTS.LAUNCH_CONTRACT', isChecked: false, state: 'default', identifier: EShortcutIdentifier.NewContract },
  ];

  /**
   * Map of option IDs to their corresponding icon names.
   */
  public readonly iconMap = iconMap;

  public readonly onGetShortcuts = effect(() => {
    if (this.shortcutsStore['getShortcutsLoaded']()) {
      untracked(() => {
        this.initializeShortcuts();
      })
    }
  })

  /**
   * Instance of RouterStore used to access the current route.
   */
  public routerStore = inject(RouterStore);

  /**
   * Signal that controls whether the component should be visible based on the current route.
   */
  public isVisible = signal<boolean>(false);

  /**
   * Reference to the main anchor element for positioning the dropdown-list.
   */
  @ViewChild('mainAnchor') mainAnchor!: ElementRef;

  /**
   * Controls the visibility of the settings dropdown-list.
   */
  public showSettingsDropdown = signal<boolean>(false);

  /**
   * List of IDs of the enabled (selected) options in the dropdown-list.
   */
  public enabledOptions: string[] = this.listOptions
    .filter(option => option.isChecked)
    .map(option => option.id.toString());

  /**
   * Event emitter for icon button clicks.
   */
  @Output() iconButtonClick = new EventEmitter<TShortcutOption>();


  ngAfterViewInit(): void {
    this.shortcutsStore.getShortcuts();
  }

  public onIconButtonClick(option: TShortcutOption): void {
    this.showSettingsDropdown.set(false);
    this.iconButtonClick.emit(option);
  }

  public toggleSettingsDropdown(): void {
    this.showSettingsDropdown.set(!this.showSettingsDropdown());
  }

  /**
   * Method called when the selection in the dropdown-list changes.
   * @param event The event containing the selected option.
   */
  public onSelectionChange(event: TDropdownSwitch): void {
    const optionId = event.id.toString();

    if (event.isChecked) {
      if (!this.enabledOptions.includes(optionId)) {
        this.enabledOptions.push(optionId);
      }
    } else {
      this.enabledOptions = this.enabledOptions.filter(id => id !== optionId);
    }
    const payload: IShortcutItem[] = this.listOptions
      .filter(option => this.enabledOptions.includes(option.id))
      .map(option => ({
        Identifier: option.identifier
      }));

    this.shortcutsStore.updateShortcuts(payload);
  }

  public initializeShortcuts(): void {
    this.listOptions = this.listOptions.map(option => {
      if (!this.shortcuts()?.length) {
        return {
          ...option,
          isChecked: option.isChecked,
        };
      }
      const shortcut = this.shortcuts().find(shortcut => shortcut.Identifier === option.identifier);
      return {
        ...option,
        isChecked: !!shortcut,
      };
    });

    this.enabledOptions = this.listOptions.filter((option) => option.isChecked).map(option => option.id);
    this.isVisible.set(true);
  }

  public removeFromUnsavedData(id: string): void {
    this.shortcutsStore.removeFromUnsavedData(id);
    this.filegroupStore.clearTemporaryPersonId(id);
  }
}
